import type { AgentService, ScreenHintPayload } from '../../api/agent';

interface AgentThreadListAdapterDeps {
  agent: AgentService;
  getScreen: () => ScreenHintPayload | undefined;
}

export function createAgentThreadListAdapter(deps: AgentThreadListAdapterDeps) {
  const { agent, getScreen } = deps;

  return {
    async list() {
      const threads = await agent.listThreads();
      return {
        threads: threads.map((thread) => ({
          status: 'regular' as const,
          remoteId: thread.id,
          externalId: thread.id,
          title: thread.title ?? undefined,
          lastMessageAt: new Date(thread.lastMessageAt ?? thread.createdAt),
        })),
      };
    },

    async initialize(threadId: string) {
      if (isUuid(threadId)) return { remoteId: threadId, externalId: threadId };

      const created = await agent.createThread(getScreen());
      return { remoteId: created.id, externalId: created.id };
    },

    async fetch(threadId: string) {
      const { thread } = await agent.getThread(threadId);
      return {
        status: 'regular' as const,
        remoteId: thread.id,
        externalId: thread.id,
        title: thread.title ?? undefined,
        lastMessageAt: new Date(thread.lastMessageAt ?? thread.createdAt),
      };
    },

    async archive(remoteId: string) {
      await agent.deleteThread(remoteId);
    },
  };
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | undefined): value is string {
  return !!value && UUID_PATTERN.test(value);
}
